/**
 * Makes text safe for the PDF standard fonts.
 *
 * pdf-lib's built-in fonts encode WinAnsi, which covers Latin-1 and the usual
 * typographic punctuation but nothing beyond it. Handed a character it cannot
 * encode, pdf-lib throws, which on a signing screen means losing a form someone
 * has already filled in and signed.
 *
 * So: map the punctuation that has a WinAnsi equivalent, strip diacritics from
 * anything that does not (Łukasz becomes Lukasz rather than failing), and fall
 * back to '?' only when there is nothing closer. Any substitution is reported so
 * the UI can warn rather than quietly altering a legal name.
 */

const DIRECT: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '‚': ',',
  '“': '"',
  '”': '"',
  '–': '-',
  '—': '-',
  '−': '-',
  '…': '...',
  ' ': ' ',
  '•': '•',
  '\t': '    ',
}

/**
 * Letters formed with a stroke or bar rather than an accent. Unicode gives
 * these no canonical decomposition, so NFD leaves them untouched and they would
 * otherwise fall through to '?'. Common enough in names to name individually.
 */
const TRANSLITERATE: Record<string, string> = {
  'Ł': 'L', // Ł
  'ł': 'l', // ł
  'Đ': 'D', // Đ
  'đ': 'd', // đ
  'Ħ': 'H', // Ħ
  'ħ': 'h', // ħ
  'Ŧ': 'T', // Ŧ
  'ŧ': 't', // ŧ
  'ı': 'i', // ı
  'ſ': 's', // ſ
  'ĳ': 'ij', // ĳ
  'Ĳ': 'IJ', // Ĳ
}

/**
 * The handful of characters WinAnsi carries above Latin-1, in the 0x80-0x9F
 * block Unicode reserves for controls. pdf-lib maps these, so they pass
 * through untouched.
 */
const WIN_ANSI_EXTRAS = new Set([
  'Œ', 'œ', // Œ œ
  'Š', 'š', // Š š
  'Ž', 'ž', // Ž ž
  'Ÿ', // Ÿ
  'ƒ', // ƒ
  '†', '‡', // † ‡
  '‰', // ‰
  '€', // €
  '™', // ™
  '‹', '›', // ‹ ›
  'ˆ', '˜', // ˆ ˜
])

/** WinAnsi covers printable ASCII and Latin-1's upper half. */
function encodable(code: number): boolean {
  return (code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff)
}

export interface SanitisedText {
  text: string
  /** True when a character could not be represented exactly. */
  substituted: boolean
}

export function sanitise(input: string): SanitisedText {
  let out = ''
  let substituted = false

  for (const char of input) {
    const direct = DIRECT[char]
    if (direct !== undefined) {
      out += direct
      continue
    }
    const code = char.codePointAt(0) ?? 0
    if (encodable(code) || WIN_ANSI_EXTRAS.has(char)) {
      out += char
      continue
    }
    const transliterated = TRANSLITERATE[char]
    if (transliterated !== undefined) {
      out += transliterated
      substituted = true
      continue
    }
    // Try to keep the letter and lose only the accent.
    const stripped = char.normalize('NFD').replace(/\p{Diacritic}/gu, '')
    if (stripped && [...stripped].every((c) => encodable(c.codePointAt(0) ?? 0))) {
      out += stripped
      substituted = true
      continue
    }
    out += '?'
    substituted = true
  }

  return { text: out, substituted }
}

/** True if any value in the record would be altered on the way into a PDF. */
export function findUnrepresentable(values: Record<string, string>): string[] {
  return Object.entries(values)
    .filter(([, value]) => sanitise(value).substituted)
    .map(([key]) => key)
}
